// Today's Needs you (AdminToday.dc.html): 1 row per job hub.today_jobs gives the caller's role, with
// its tick box, its chip, its words (words.js), its link and its buttons, and the bold line under it
// once a button has done its work. A ticked job stays where it is, faded, until midnight (the read
// gives the day's ticked jobs), with who ticked it and when, which the database stamps.
//
// The buttons follow the job's reason (the stage 5 critic's wrong_or_contradictory 9): an unmapped
// price is mapped on the Price map and then applied here; a payment with no email or waiting for its
// member is applied to a new member or matched to someone. A payment with no access and access with no
// payment are the Price map's jobs too: their words, buttons and outcomes are src/admin/mismatches.js's,
// so the 2 screens agree. Each write is the contract's
// (data-model 10.4): ticking off and its resolution, a new balance date, a reason and a certificate
// revoked are direct writes under row level security; the card link, a reply, ending or revoking
// access, applying a payment, an export and a deletion are the stage 5 functions. Support and hosts
// get only what their role can do; the database refuses the rest anyway.
//
// A mastermind's own jobs (REVIEW, SET UP, and a register row of its own: src/admin/today/mastermindJobs.js)
// have no flag behind them, so they have no tick box: each goes once what raised it is done.
import { useEffect, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { Button, ConfirmPanel, Field, Loading, Problem, SearchBox, TextArea, TextInput } from '../ui/index.js'
import { addDays, todayInUk, ukToIso } from '../ui/format.js'
import { uk } from '../../lib/format.js'
import { jobCard, doneLine, doneStamp, firstOf, fullOf, possessive, productIn } from './words.js'
import { MISMATCH_WORDS as MW, RESOLUTION, emailOk, reasonLabel, reasonHint, revokeAsk } from '../mismatches.js'

const REPLY_MAX = 4000

export function NeedsYou({ rows, status, error, reload, msgs, ctx, onTick, onOp }) {
  return (
    <section className="td-card" aria-labelledby="td-jobs-h">
      <div className="td-card-head td-card-head--split">
        <h2 className="td-h2" id="td-jobs-h">Needs you</h2>
        <span className="td-card-aside">Tick it off when it's done</span>
      </div>
      {status === 'loading' && !rows && <Loading />}
      {status === 'error' && !rows && <div className="td-pad"><Problem error={error} onRetry={reload} /></div>}
      {rows && !rows.length && <p className="td-empty">Nothing needs you today.</p>}
      {rows?.length > 0 && (
        <ul className="td-jobs" aria-label="Needs you">
          {rows.map(job => (
            <JobRow key={job.job_key} job={job} ctx={ctx} msg={msgs[job.job_key]} onTick={onTick} onOp={onOp} />
          ))}
        </ul>
      )}
    </section>
  )
}

// The panels a button opens in its row before anything is written
const PANEL_OPS = new Set(['reply', 'create_member_email', 'match', 'add_reason', 'decline', 'end_access', 'revoke', 'revoke_certificate', 'delete', 'handled', 'intro_paid'])

function JobRow({ job, ctx, msg, onTick, onOp }) {
  const card = jobCard(job, ctx)
  const done = job.status === 'done'
  const [panel, setPanel] = useState(null)
  const [busy, setBusy] = useState(null)
  const actions = card.actions.filter(a => a.roles.includes(ctx.role))
  const stamp = doneStamp(job)

  async function run(op, args) {
    if (busy) return false
    setBusy(op)
    try {
      const ok = await onOp(op, job, card, args)
      if (ok) setPanel(null)
      return ok
    } finally {
      setBusy(null)
    }
  }

  function press(a) {
    if (PANEL_OPS.has(a.op)) setPanel(p => (p === a.op ? null : a.op))
    else run(a.op)
  }

  return (
    <li className={`td-job${done ? ' td-job--done' : ''}`}>
      {job.flag_ids?.length
        ? <input type="checkbox" className="td-tick" aria-label={`Mark ${card.title} as done`} checked={done}
          onChange={e => onTick(job, e.target.checked)} />
        : <span className="td-tick-space" aria-hidden="true" />}
      <div className="td-job-main">
        <div className="td-job-text">
          <span className="td-job-top">
            <span className={`td-chip${card.ink ? ' td-chip--ink' : ''}`}>{card.chip}</span>
            <span className="td-job-title">{card.title}</span>
          </span>
          {card.detail && <span className="td-job-detail">{card.detail}</span>}
          {card.note && <span className="td-job-detail">{card.note}</span>}
        </div>
        {done && stamp && <span className="td-job-stamp">{stamp}</span>}
        {msg && <span className={`td-job-msg${msg.tone === 'problem' ? ' td-job-msg--problem' : ''}`} role={msg.tone === 'problem' ? 'alert' : 'status'}>{msg.text}</span>}
        {panel && (
          <Panel op={panel} job={job} card={card} ctx={ctx} busy={busy === panel}
            onCancel={() => setPanel(null)} onRun={args => run(panel, args)} />
        )}
      </div>
      {(card.link || actions.length > 0) && (
        <div className="td-job-acts">
          {card.link && <Link to={card.link.to} className="td-job-link">{card.link.label}</Link>}
          {actions.map(a => (
            <button key={a.op} type="button" className={`td-job-btn${a.kind === 'line' ? ' td-job-btn--line' : ''}`}
              aria-expanded={PANEL_OPS.has(a.op) ? panel === a.op : undefined}
              disabled={Boolean(busy)} aria-busy={busy === a.op || undefined} onClick={() => press(a)}>{a.label}</button>
          ))}
        </div>
      )}
    </li>
  )
}

// What a button opens in its row: a box to write in, someone to pick, or a question before something
// that can't be taken back
function Panel({ op, job, card, ctx, busy, onCancel, onRun }) {
  const first = firstOf(job)
  switch (op) {
    case 'reply': return <ReplyPanel job={job} busy={busy} onCancel={onCancel} onRun={onRun} />
    case 'create_member_email': return <EmailPanel busy={busy} onCancel={onCancel} onRun={onRun} />
    case 'match': return <MatchPanel ctx={ctx} busy={busy} onCancel={onCancel} onRun={onRun} />
    case 'add_reason': return <ReasonPanel job={job} busy={busy} onCancel={onCancel} onRun={onRun} />
    case 'decline': return <DeclinePanel job={job} busy={busy} onCancel={onCancel} onRun={onRun} />
    case 'intro_paid': return <IntroPaidPanel busy={busy} onCancel={onCancel} onRun={onRun} />
    case 'end_access':
      return (
        <ConfirmPanel busy={busy} onCancel={onCancel} onConfirm={() => onRun()} cancelLabel="Cancel"
          confirmLabel={`End ${possessive(first)} access`}
          text={`End ${possessive(first)} access to ${productIn(job)}? Progress is kept${job.kind === 'refund' ? ', and nothing of it stays for life after a refund' : ''}.`} />
      )
    case 'revoke':
      return (
        <ConfirmPanel busy={busy} onCancel={onCancel} onConfirm={() => onRun()} confirmLabel={MW.revokeAccess} cancelLabel={MW.keepIt}
          text={revokeAsk(job)} />
      )
    case 'handled':
      return (
        <ConfirmPanel busy={busy} onCancel={onCancel} onConfirm={() => onRun()} confirmLabel={MW.handled} cancelLabel={MW.keepIt}
          text={MW.handledAsk} />
      )
    case 'revoke_certificate': {
      const number = job.certificate?.number || job.detail?.number
      return (
        <ConfirmPanel busy={busy} onCancel={onCancel} onConfirm={() => onRun()} confirmLabel="Revoke the certificate"
          text={`Revoke certificate ${number} for ${fullOf(job)}? Its check link then finds nothing.`} />
      )
    }
    case 'delete':
      return (
        <ConfirmPanel busy={busy} onCancel={onCancel} onConfirm={() => onRun()} cancelLabel="Cancel" confirmLabel={`Delete ${first}`}
          text={`Delete ${fullOf(job)}? ${possessive(first)} details are wiped and access to everything ends. It can't be undone.`} />
      )
    default: return null
  }
}

function ReplyPanel({ job, busy, onCancel, onRun }) {
  const [text, setText] = useState('')
  const [error, setError] = useState(null)
  const first = firstOf(job)
  function send() {
    const t = text.trim()
    if (!t) return setError('Type a reply first.')
    if (t.length > REPLY_MAX) return setError('Keep the reply to 4,000 characters.')
    setError(null)
    return onRun({ reply: t })
  }
  return (
    <div className="td-panel">
      <Field label={`Reply to ${first}`} plain error={error}
        hint={job.person_email ? `It goes to ${job.person_email} by email, with the question and a link back to where it was asked.` : undefined}>
        {props => <TextArea {...props} rows={4} value={text} onChange={setText} maxLength={REPLY_MAX} />}
      </Field>
      <span className="td-panel-acts">
        <Button kind="ink" size="sm" busy={busy} busyLabel="Sending" onClick={send}>Send the reply</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
      </span>
    </div>
  )
}

function EmailPanel({ busy, onCancel, onRun }) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  function go() {
    const e = email.trim()
    if (!emailOk(e)) return setError(MW.emailWrong)
    setError(null)
    return onRun({ email: e })
  }
  return (
    <div className="td-panel">
      <Field label={MW.email} plain error={error} hint={MW.emailHint}>
        {props => <TextInput {...props} type="email" value={email} onChange={setEmail} autoComplete="off" />}
      </Field>
      <span className="td-panel-acts">
        <Button kind="ink" size="sm" busy={busy} busyLabel={MW.creating} onClick={go}>{MW.create}</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>{MW.cancel}</Button>
      </span>
    </div>
  )
}

// Find someone already in the hub, by name or email (hub.staff_people), and apply the payment to them
function MatchPanel({ ctx, busy, onCancel, onRun }) {
  const [q, setQ] = useState('')
  const [found, setFound] = useState({ status: 'idle', rows: [] })
  const [pick, setPick] = useState(null)
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setFound({ status: 'idle', rows: [] }); return undefined }
    let alive = true
    const t = setTimeout(() => {
      setFound(f => ({ ...f, status: 'loading' }))
      ctx.api.staff_people({ search: term, limit: 6 }).then(
        rows => { if (alive) setFound({ status: 'ready', rows }) },
        e => { if (alive) setFound({ status: 'error', rows: [], error: ctx.api.words(e) }) },
      )
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [q, ctx.api])
  const chosen = found.rows.find(r => r.person_id === pick)
  return (
    <div className="td-panel">
      <SearchBox label={MW.search} value={q} onChange={v => { setQ(v); setPick(null) }} />
      {found.status === 'loading' && <span className="td-panel-note" role="status">Looking.</span>}
      {found.status === 'error' && <span className="ad-error" role="alert">{found.error}</span>}
      {found.status === 'ready' && !found.rows.length && <span className="td-panel-note">{MW.nobody}</span>}
      {found.rows.length > 0 && (
        <div className="td-match" role="radiogroup" aria-label="Who it's for">
          {found.rows.map(r => (
            <button key={r.person_id} type="button" role="radio" aria-checked={pick === r.person_id} className="td-match-row"
              onClick={() => setPick(r.person_id)}>
              <span className="td-match-name">{r.full_name}</span>
              <span className="td-match-email">{r.email}</span>
            </button>
          ))}
        </div>
      )}
      <span className="td-panel-acts">
        <Button kind="ink" size="sm" busy={busy} busyLabel="Matching" disabled={!chosen}
          onClick={() => onRun({ person_id: chosen.person_id, full_name: chosen.full_name })}>
          {chosen ? `Match to ${chosen.full_name}` : MW.match}
        </Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>{MW.cancel}</Button>
      </span>
    </div>
  )
}

function ReasonPanel({ job, busy, onCancel, onRun }) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState(null)
  function go() {
    const r = reason.trim()
    if (!r) return setError(MW.reasonNeeded)
    setError(null)
    return onRun({ reason: r })
  }
  return (
    <div className="td-panel">
      <Field label={reasonLabel(job)} plain error={error} hint={reasonHint(job)}>
        {props => <TextInput {...props} value={reason} onChange={setReason} maxLength={1000} />}
      </Field>
      <span className="td-panel-acts">
        <Button kind="ink" size="sm" busy={busy} busyLabel={MW.saving} onClick={go}>{MW.saveReason}</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>{MW.cancel}</Button>
      </span>
    </div>
  )
}

// Mark paid on a referral's owed card (decision 55): the bank transfer's reference, if there is 1, then
// hub.mark_introduction_paid, which closes the card's flag
function IntroPaidPanel({ busy, onCancel, onRun }) {
  const [reference, setReference] = useState('')
  return (
    <div className="td-panel">
      <Field label="Payment reference (optional)" plain>
        {props => <TextInput {...props} value={reference} onChange={setReference} maxLength={80} autoComplete="off" />}
      </Field>
      <span className="td-panel-acts">
        <Button kind="ink" size="sm" busy={busy} busyLabel="Marking" onClick={() => onRun({ reference: reference.trim() })}>Mark paid</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
      </span>
    </div>
  )
}

function DeclinePanel({ job, busy, onCancel, onRun }) {
  const [note, setNote] = useState('')
  const first = firstOf(job)
  return (
    <div className="td-panel">
      <Field label="A note, if you want 1" plain hint={`Only staff see it. Nothing is emailed to ${first}, so tell ${first} yourself.`}>
        {props => <TextArea {...props} rows={2} value={note} onChange={setNote} maxLength={1000} />}
      </Field>
      <span className="td-panel-acts">
        <Button kind="ink" size="sm" busy={busy} busyLabel="Declining" onClick={() => onRun({ note: note.trim() })}>Decline the request</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
      </span>
    </div>
  )
}

// The writes behind each button, as the contract has them (data-model 10.4). Each answers the bold
// line to show under the job; a refusal throws its own words. tick() ticks the job off with a
// resolution once the write it follows has gone through.
export function makeOps(api, { nowMs } = {}) {
  const flagId = job => job.flag_ids?.[0]
  const tick = (job, resolution) => api.tick_job(job.flag_ids, { resolution }).catch(() => null)

  return {
    async card_link(job) {
      const answer = await api.send_card_link({ person_id: job.person_id, access_id: job.access_id || undefined })
      return doneLine('card_link', job, answer)
    },
    async reply(job, card, { reply }) {
      const answer = await api.reply_question({ question_id: job.question_id, reply })
      return doneLine('reply', job, answer)
    },
    async messaged(job) {
      await api.tick_job(job.flag_ids, { resolution: 'Marked as messaged' })
      return doneLine('messaged', job)
    },
    async keep_access(job) {
      await api.tick_job(job.flag_ids, { resolution: 'Access kept' })
      return doneLine('keep_access', job)
    },
    async end_access(job) {
      const answer = await api.revoke_access({ access_id: job.access_id, end_reason: job.kind === 'refund' ? 'refund_ended' : 'revoked_by_staff' })
      await tick(job, 'Access ended')
      return answer.message
    },
    async revoke(job) {
      await api.revoke_access({ access_id: job.access_id, end_reason: 'revoked_by_staff' })
      await tick(job, RESOLUTION.revoked)
      return doneLine('revoke', job)
    },
    async handled(job) {
      await api.tick_job(job.flag_ids, { resolution: RESOLUTION.handled })
      return doneLine('handled', job)
    },
    async give_7_days(job) {
      // 7 days from today, at the time of day the balance was due
      const was = job.access_balance_due_at || job.detail?.balance_due_at
      const u = uk(was)
      const hhmm = `${String(u.hh).padStart(2, '0')}:${String(u.mm).padStart(2, '0')}`
      const due = ukToIso(addDays(todayInUk(nowMs ?? Date.now()), 7), hhmm)
      await api.update_access(job.access_id, { balance_due_at: due })
      await tick(job, 'Given 7 more days')
      return doneLine('give_7_days', job, null, { due, nowMs })
    },
    async add_reason(job, card, { reason }) {
      await api.update_access(job.access_id, { reason })
      await tick(job, RESOLUTION.reason)
      return doneLine('add_reason', job, null, { reason })
    },
    async revoke_certificate(job) {
      const id = job.certificate?.certificate_id || job.detail?.certificate_id
      const rows = await api.run(api.from('certificates').update({ revoked_at: new Date().toISOString() })
        .eq('id', id).is('revoked_at', null).select('id, revoked_at'))
      if (!rows?.length) throw new api.AdminError('not_there', 0, api.WORDS.notThere)
      await tick(job, 'Certificate revoked')
      return doneLine('revoke_certificate', job, null, { number: job.certificate?.number || job.detail?.number })
    },
    async apply_payment(job) {
      return (await api.resolve_payment({ flag_id: flagId(job) })).message
    },
    async create_member(job) {
      return (await api.resolve_payment({ flag_id: flagId(job) })).message
    },
    async create_member_email(job, card, { email }) {
      return (await api.resolve_payment({ flag_id: flagId(job), email })).message
    },
    async match(job, card, { person_id }) {
      return (await api.resolve_payment({ flag_id: flagId(job), person_id })).message
    },
    async export(job) {
      return (await api.data_export({ request_id: job.data_request?.data_request_id || job.detail?.data_request_id })).message
    },
    async decline(job, card, { note }) {
      return (await api.data_export({ request_id: job.data_request?.data_request_id || job.detail?.data_request_id, decline: true, note: note || undefined })).message
    },
    // referrals (decision 55, migration 79): each write closes the card's flag in the database
    async intro_accept(job) {
      await api.decide_introduction({ id: job.detail?.introduction_id, accept: true })
      return doneLine('intro_accept', job)
    },
    async intro_turn_down(job) {
      await api.decide_introduction({ id: job.detail?.introduction_id, accept: false })
      return doneLine('intro_turn_down', job)
    },
    async intro_not_proceeding(job) {
      await api.set_introduction_status({ id: job.detail?.introduction_id, status: 'not_proceeding' })
      return doneLine('intro_not_proceeding', job)
    },
    async intro_paid(job, card, { reference }) {
      await api.mark_introduction_paid({ id: job.detail?.introduction_id, reference: reference || '' })
      return doneLine('intro_paid', job)
    },
    async delete(job) {
      return (await api.delete_person({ person_id: job.person_id })).message
    },
  }
}
